import { CaredType } from '../cared/cared'
import { Qualification } from '../caretaker/professionalCertification/professionalCertification.model'
import { isEmpty } from '$utils/objectIsEmpty'
import { QualificationType } from '$domain/model/caretaker/caretaker.model'

export interface CaretakerRecommendationDto {
  availableCaretakers: {
    userId: string
    caretakerId: string
    hasActiveProSubscription: boolean
    hasVerifiedBankData: boolean
    isVerified: boolean
    caretakerHourlyRate: string
    requestId: string
    name: string
    rating: number
    price: number
    profilePicture: string
    qualificationType: string
    qualificationSpecialty: string
    qualificationArea: string
    qualificationName: string
    presentationMessage: string
    careType: string[]
  }[]
  serviceDates: { startingDateTime: string; finishingDateTime: string }[]
  serviceLength: number
  serviceLocation: string
  caredType: CaredType
  age: number
  isRecommendation: boolean
  nameOfSelectedCaretaker: string
  isClientSelectedCaretakerAvailable: boolean
  hiddenAvailableCaretakersAmount: number
  hiddenPresentationMessagesAmount: number
  token: string
}

export interface CaretakerRecommendationViewModel {
  service: ServiceDetails
  recommendations: CaretakersRecommendation[]
}

export interface RecommendedCaretaker {
  userId: string
  caretakerId: string
  requestId: string
  name: string
  rating: number
  ratingAmount: number
  profilePicture?: string
  location: string
  languages: string[]
  mainQualification: Qualification
  price: number
  caretakerHourlyRate: number
  hasActiveProSubscription: boolean
  hasVerifiedBankData: boolean
}

export interface ServiceDetails {
  serviceDates: { startingDateTime: Date; finishingDateTime: Date }[]
  serviceLength: number
  location: string
  caredType: number
  age: number
  isRecommendation: boolean
  nameOfSelectedCaretaker: string
  isClientSelectedCaretakerAvailable: boolean
  residentName?: string
  residentSurname?: string
  hiddenAvailableCaretakersAmount?: number
  hiddenPresentationMessagesAmount?: number
  token?: string
  budgetFee?: number
  basePrice?: number
}

export interface CaretakersRecommendation {
  userId: string
  caretakerId: string
  requestId: string
  name: string
  rating: number
  profilePicture: string
  mainQualification: { qualificationName: string; specialty: string; type: string; area: string }
  price: number
  caretakerHourlyRate: string
  presentationMessage: string
  hasActiveProSubscription: boolean
  hasVerifiedBankData: boolean
  isVerified: boolean
  careType: {
    adult: boolean
    child: boolean
    extra: boolean
  }
}

enum Languages {
  EUSKERA = 'EUS',
  ESPAÑOL = 'ES',
  CATALÁN = 'CAT',
  GALLEGO = 'GL',
  INGLÉS = 'EN',
  FRANCÉS = 'FR',
  PORTUGUÉS = 'PT',
  ALEMÁN = 'DE',
  RUSO = 'RU',
  CHINO = 'CHI',
}

const isUniversityQualification = type => type === QualificationType.universityEducation

const isProfessionalQualification = type => type === 'Formación profesional' || QualificationType.jobTraining

const isUniversityOrProfessionalQualification = type =>
  isUniversityQualification(type) || isProfessionalQualification(type)

const isCourseQualification = type => type === QualificationType.course

const isOtherQualificationSpeciality = specialty => specialty === 'Otra'

export const getQualificationResume = (qualification: Qualification) => {
  if (isCourseQualification(qualification.type) && qualification.qualificationName) {
    return `Curso - ${qualification.qualificationName}`
  }

  if (isCourseQualification(qualification.type) && qualification.specialty && !qualification.qualificationName) {
    return `Curso - ${qualification.specialty}`
  }

  if (
    isUniversityOrProfessionalQualification(qualification.type) &&
    !isOtherQualificationSpeciality(qualification.specialty)
  ) {
    return qualification.specialty
  }

  if (
    isUniversityOrProfessionalQualification(qualification.type) &&
    isOtherQualificationSpeciality(qualification.specialty) &&
    qualification.qualificationName
  ) {
    return qualification.qualificationName
  }

  if (
    isUniversityOrProfessionalQualification(qualification.type) &&
    isOtherQualificationSpeciality(qualification.specialty) &&
    qualification.area &&
    !qualification.qualificationName
  ) {
    return isUniversityQualification(qualification.type)
      ? `Formación universitaria :${qualification.area}`
      : `Formación profesional :${qualification.area}`
  }

  if (qualification.specialty && !isOtherQualificationSpeciality(qualification.specialty)) {
    return qualification.specialty
  }

  return ''
}

const getAbbreviatedLanguage = (language: string) => {
  if (!Languages[language.toUpperCase()]) {
    return language
  }

  return Languages[language.toUpperCase()]
}

export const abbreviateLanguages = (languages: string[]) => {
  if (isEmpty(languages)) {
    languages.push('ES')
  }
  return languages.map(language => getAbbreviatedLanguage(language))
}

export const parseMinutesToHoursAndMinutes = (duracion: number) => {
  if (!duracion) {
    return { hours: undefined, minutes: undefined }
  }
  const hours = Math.floor(duracion / 60)
  const minutes = duracion % 60

  if (minutes === 0) {
    return `${hours}h`
  }
  return `${hours}h ${minutes}m`
}

export const recommendedCaretakerHasRating = (recommendedCaretaker: RecommendedCaretaker) =>
  Boolean(recommendedCaretaker.ratingAmount)

export interface CaretakerReview {
  name: string
  title: string
  comment: string
  rating: number
  date: Date
}

export interface RecommendationService {
  locality: string
  latitude: number
  longitude: number
  serviceDays: any
  cares: { companionship: number; sanityAttention: number; cleaness: number; basics: number; transport: number }
  age: number
  careeType: number
  extraInformation: string
  province: string
  gender: number
}

export interface RecruitingService {
  institutionName: string
  institutionType: string
  locality: string
  address: string
  latitude: number
  longitude: number
  startDate: string
  contractType: string
  extraInformation: string
  professionalProfile: {
    auxiliaryNurse: boolean
    nurse: boolean
    dependencyPositionAttendance: boolean
    otherProfile: string
  }
}

export interface RecommendationServiceOutSession {
  locality: string
  latitude: number
  longitude: number
  serviceDays: any
  cares: { companionship: number; sanityAttention: number; cleaness: number; basics: number; transport: number }
  age: number
  careeType: number
  extraInformation: string
  province: string
  gender: number
  name: string
  phone: string
  email: string
}

export interface BroadcastService {
  latitude: number
  longitude: number
  address: string
  province: string
  information: string
  phoneNumber: string
  careType: string
  hoursPerWeek: string
  name?: string
  email?: string
}

export interface BroadcastServiceDto {
  latitude: number
  longitude: number
  address: string
  information: string
  province: string
  phoneNumber: string
  careType: string
  hoursPerWeek: string
  name?: string
  email?: string
}

export interface ResidenceRecommendationService {
  latitude: number
  longitude: number
  address: string
  province: string
  serviceDates: {
    date: Date
    hours: {
      start: string
      duration: string
    }[]
  }[]
  residentId: string
  companionship?: boolean
  sanityAttention?: boolean
  cleaness?: boolean
  transport?: boolean
  extraInformation?: string
  caretakerId?: string
}

export interface ResidenceRecommendationServiceDto {
  latitude: number
  longitude: number
  address: string
  province: string
  serviceDates: {
    date: Date | string
    hours: {
      start: string
      duration: string
    }[]
  }[]
  residentId: string
  cares: {
    companionship: number
    sanityAttention: number
    cleaness: number
    transport: number
  }
  extraInformation: string
  caretakerId?: string
}



// WEBPACK FOOTER //
// ./src/domain/model/recommendation/recommendation.model.ts